import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import AdminLink from '../components/AdminLink';
import { apiFetch } from '../jscontrol/apiClient';
import '../css/Category.css';
import CategoryCreate from './CategoryCreate';
import CategoryEdit from './CategoryEdit';

const CategoryPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'danger') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };
  const [filterText, setFilterText] = useState('');
  const [sortKey, setSortKey] = useState('CategoryName');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [PAGE_SIZE, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const fetchCategories = async (currentPage, pageSize, search, sk, sd) => {
    setLoading(true);
    setError(null);
    try {
      const sortColumn = sk === 'ParentCategory' ? 'ParentCategoryId' : sk === 'Status' ? 'IsActive' : sk;
      const params = new URLSearchParams({
        pageIndex: currentPage,
        pageSize: pageSize,
        search: search || '',
        sortColumn: sortColumn,
        sortBy: sd.toUpperCase(),
      });
      const { data } = await apiFetch(`/Category/GetAll?${params}`);
      const items = data?.data ?? [];
      const count = data?.totalRecords ?? data?.totalCount ?? (Array.isArray(items) ? items.length : 0);
      setCategories(Array.isArray(items) ? items : []);
      setTotalCount(count);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories(page, PAGE_SIZE, filterText, sortKey, sortDir);
  }, [page, PAGE_SIZE, filterText, sortKey, sortDir]);

  const openAdd = () => {
    setShowAddModal(true);
  };

  const openEdit = (cat) => {
    setEditItem(cat);
    setShowEditModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await apiFetch(`/Category/Delete/${id}`, { method: 'DELETE' });
      showToast('Category deleted successfully.', 'success');
      fetchCategories(page, PAGE_SIZE, filterText, sortKey, sortDir);
    } catch (e) {
      showToast(e.message);
    }
  };

  const parentMap = {};
  categories.forEach(c => { parentMap[c.CategoryId] = c.CategoryName; });

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  const goToPage = (p) => setPage(Math.max(1, Math.min(p, totalPages)));

  const handleFilter = (val) => { setFilterText(val); setPage(1); };

  const handleSortCol = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const handlePageSize = (val) => { setPageSize(Number(val)); setPage(1); };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <i className="mdi mdi-sort ms-1" style={{ opacity: 0.3 }}></i>;
    return sortDir === 'asc'
      ? <i className="mdi mdi-sort-ascending ms-1" style={{ color: '#6f42c1' }}></i>
      : <i className="mdi mdi-sort-descending ms-1" style={{ color: '#6f42c1' }}></i>;
  };

  return (
    <AdminLayout>
      <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6c757d' }}>
        <AdminLink to="/admin">Dashboard</AdminLink>
        <span style={{ margin: '0 6px' }}>/</span>
        <span>Categories</span>
      </p>

      <div className="row">
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              {/* Toast notification */}
              {toast && (
                <div
                  className={`alert alert-${toast.type} alert-dismissible d-flex align-items-center py-2 mb-3`}
                  role="alert"
                  style={{ fontSize: '0.875rem' }}
                >
                  <i className={`mdi ${toast.type === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'} me-2`} style={{ fontSize: '1.1rem' }}></i>
                  <span>{toast.message}</span>
                  <button
                    type="button"
                    className="btn-close ms-auto"
                    style={{ fontSize: '0.7rem' }}
                    onClick={() => setToast(null)}
                    aria-label="Close"
                  />
                </div>
              )}

              {/* header row: title + filter + add button */}
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h4 className="card-title mb-0">Category</h4>
                <div className="d-flex align-items-center gap-2">
                  <div className="cat-filter-wrap">
                    <i className="mdi mdi-magnify cat-filter-icon"></i>
                    <input
                      type="text"
                      className="cat-filter-input"
                      placeholder="Search category..."
                      value={filterText}
                      onChange={e => handleFilter(e.target.value)}
                    />
                  </div>
                  <button className="cat-add-btn" onClick={openAdd}>
                    <i className="mdi mdi-plus" style={{ marginRight: 6 }}></i> Add Category
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : error ? (
                <div className="alert alert-danger d-flex align-items-center py-2" style={{ fontSize: '0.875rem' }}>
                  <i className="mdi mdi-alert-circle me-2" style={{ fontSize: '1.1rem' }}></i>
                  {error}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th className="cat-sort-th" onClick={() => handleSortCol('CategoryName')} style={{ width: '25%' }}>
                          Category Name <SortIcon col="CategoryName" />
                        </th>
                        <th className="cat-sort-th" onClick={() => handleSortCol('ParentCategory')} style={{ width: '22%' }}>
                          Parent Category <SortIcon col="ParentCategory" />
                        </th>
                        <th className="cat-sort-th" onClick={() => handleSortCol('DisplayOrder')} style={{ width: '14%' }}>
                          Display Order <SortIcon col="DisplayOrder" />
                        </th>
                        <th className="cat-sort-th" onClick={() => handleSortCol('Status')} style={{ width: '12%' }}>
                          Status <SortIcon col="Status" />
                        </th>
                        <th style={{ width: '12%' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categories.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center py-4 text-muted">
                            No categories found.
                          </td>
                        </tr>
                      ) : categories.map((cat) => (
                        <tr key={cat.CategoryId}>
                          <td className="cat-name-cell">{cat.CategoryName}</td>
                          <td>
                            {cat.ParentCategoryId
                              ? (parentMap[cat.ParentCategoryId] || cat.ParentCategoryId)
                              : <span className="text-muted">—</span>}
                          </td>
                          <td>{cat.DisplayOrder}</td>
                          <td>
                            <label className={`badge ${cat.IsActive ? 'badge-success' : 'badge-danger'}`}>
                              {cat.IsActive ? 'Active' : 'Inactive'}
                            </label>
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary me-1"
                              style={{ border: 'none' }}
                              onClick={() => openEdit(cat)}
                              title="Edit"
                            >
                              <i className="mdi mdi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              style={{ border: 'none' }}
                              onClick={() => handleDelete(cat.CategoryId)}
                              title="Delete"
                            >
                              <i className="mdi mdi-delete"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {!loading && !error && totalCount > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <span className="cat-page-info">Rows per page:</span>
                    <select
                      className="cat-pagesize-select"
                      value={PAGE_SIZE}
                      onChange={e => handlePageSize(e.target.value)}
                    >
                      <option value={10}>10</option>
                      <option value={30}>30</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="cat-page-info">
                      Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, totalCount)} of {totalCount}
                    </span>
                  </div>
                  <div className="cat-pagination">
                    <button className="cat-page-btn" onClick={() => goToPage(1)} disabled={safePage === 1} title="First">
                      <i className="mdi mdi-chevron-double-left"></i>
                    </button>
                    <button className="cat-page-btn" onClick={() => goToPage(safePage - 1)} disabled={safePage === 1} title="Previous">
                      <i className="mdi mdi-chevron-left"></i>
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                      .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, i) =>
                        p === '...' ? (
                          <span key={`e${i}`} className="cat-page-ellipsis">…</span>
                        ) : (
                          <button
                            key={p}
                            className={`cat-page-btn ${safePage === p ? 'active' : ''}`}
                            onClick={() => goToPage(p)}
                          >
                            {p}
                          </button>
                        )
                      )}

                    <button className="cat-page-btn" onClick={() => goToPage(safePage + 1)} disabled={safePage === totalPages} title="Next">
                      <i className="mdi mdi-chevron-right"></i>
                    </button>
                    <button className="cat-page-btn" onClick={() => goToPage(totalPages)} disabled={safePage === totalPages} title="Last">
                      <i className="mdi mdi-chevron-double-right"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showAddModal && (
        <CategoryCreate
          categories={categories}
          onClose={() => setShowAddModal(false)}
          onSuccess={(msg) => {
            setShowAddModal(false);
            showToast(msg, 'success');
            fetchCategories(page, PAGE_SIZE, filterText, sortKey, sortDir);
          }}
        />
      )}

      {showEditModal && editItem && (
        <CategoryEdit
          categories={categories}
          editItem={editItem}
          onClose={() => { setShowEditModal(false); setEditItem(null); }}
          onSuccess={(msg) => {
            setShowEditModal(false);
            setEditItem(null);
            showToast(msg, 'success');
            fetchCategories(page, PAGE_SIZE, filterText, sortKey, sortDir);
          }}
        />
      )}
    </AdminLayout>
  );
};

export default CategoryPage;
